import { Link, useInRouterContext } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import './LegalLinks.css';

/** Public paths of the legal pages (see App.tsx). */
export const LEGAL_PATHS = {
  imprint: '/imprint',
  privacy: '/privacy',
  terms: '/terms',
} as const;

export interface LegalLinksProps {
  className?: string;
  /**
   * Render as a labelled `<nav>` landmark (default). Pass false inside a
   * region that already has its own navigation landmark (the app sidebar).
   */
  landmark?: boolean;
}

/**
 * "Imprint · Privacy · Terms" navigation, shown in the public layout footer
 * and the app sidebar footer. Falls back to plain anchors outside a router.
 */
export function LegalLinks({ className = '', landmark = true }: LegalLinksProps) {
  const { t } = useTranslation();
  const inRouter = useInRouterContext();
  const items = [
    { to: LEGAL_PATHS.imprint, label: t('legal.footer.imprint') },
    { to: LEGAL_PATHS.privacy, label: t('legal.footer.privacy') },
    { to: LEGAL_PATHS.terms, label: t('legal.footer.terms') },
  ];

  const classes = ['rg-legal-links', className].filter(Boolean).join(' ');
  const list = (
    <ul className="rg-legal-links__list">
      {items.map((item) => (
        <li key={item.to} className="rg-legal-links__item">
          {inRouter ? (
            <Link to={item.to} className="rg-legal-links__link">{item.label}</Link>
          ) : (
            <a href={item.to} className="rg-legal-links__link">{item.label}</a>
          )}
        </li>
      ))}
    </ul>
  );

  return landmark ? (
    <nav aria-label={t('legal.footer.label')} className={classes}>{list}</nav>
  ) : (
    <div className={classes}>{list}</div>
  );
}

export default LegalLinks;
